import React, { useState, ChangeEvent, useEffect } from 'react'
import { Modal, Button, Form, ProgressBar, Spinner, Row, Col } from 'react-bootstrap'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { useUploadFile } from '../../../hooks/useUploadFile'
import { CREATE_RESOURCE_CENTER_STR } from '../../../gql/mutations/resourceCenterMutations'
import { toast } from 'react-toastify'
import { ICreateResourceCenterResponse } from '../../../types/resourceCenter'

interface CampaignMediaUploadModalProps {
    isOpen: boolean
    onClose: () => void
    refetch: () => void
}

const validationSchema = Yup.object().shape({
    name: Yup.string().required('Name is required'),
    file: Yup.mixed().required('File is required'),
})

export const CampaignMediaUploadModal: React.FC<CampaignMediaUploadModalProps> = ({
    isOpen,
    onClose,
    refetch,
}) => {
    const [uploadFile, { data, error, loading }] = useUploadFile<ICreateResourceCenterResponse>()
    const [uploadProgress, setUploadProgress] = useState(0)
    const [preview, setPreview] = useState<string | null>(null)

    const formik = useFormik({
        initialValues: {
            name: '',
            file: null as File | null,
            isActive: true, // "Visible for others" / Public
        },
        validationSchema,
        onSubmit: async (v) => {
            if (!v.file) return

            setUploadProgress(0)
            const formdata = new FormData()

            // File is always mapped to '0'
            formdata.append('0', v.file, v.file.name)

            // GraphQL map for upload
            const map = { '0': ['variables.resourceData.file'] }

            // infer resourceType from mime type if possible, else default to 'document'
            // For campaigns media, we mostly care about images, but let's be safe.
            let resourceType = 'document'
            if (v.file.type.startsWith('image/')) resourceType = 'image'
            else if (v.file.type.startsWith('video/')) resourceType = 'video'

            const operations = {
                query: CREATE_RESOURCE_CENTER_STR,
                variables: {
                    resourceData: {
                        name: v.name,
                        file: null, // processed by upload scalar
                        isActive: v.isActive,
                        isCampaignMedia: true, // Mark as campaign media to separate from Resource Center
                        resourceType: resourceType,
                        // Defaults for other unrelated fields
                        description: '',
                        order: 0,
                    },
                },
            }

            formdata.append('map', JSON.stringify(map))
            formdata.append('operations', JSON.stringify(operations))

            try {
                setUploadProgress(30)
                await uploadFile(formdata)
                setUploadProgress(100)
            } catch (err) {
                console.error('Upload failed', err)
                toast.error('Upload failed')
                setUploadProgress(0)
            }
        },
    })

    // Auto-generate name from file
    const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.currentTarget.files?.[0]
        if (file) {
            formik.setFieldValue('file', file)
            if (!formik.values.name) {
                // remove extension for default name
                const nameWithoutExt = file.name.replace(/\.[^/.]+$/, "")
                formik.setFieldValue('name', nameWithoutExt)
            }
            // Preview
            if (file.type.startsWith('image/')) {
                const url = URL.createObjectURL(file)
                setPreview(url)
            } else {
                setPreview(null)
            }
        }
    }

    // Success/Error handling
    useEffect(() => {
        if (data?.createResourceCenter?.success) {
            toast.success('Media uploaded successfully!')
            refetch()
            handleClose()
        } else if (data?.createResourceCenter?.errors?.length) {
            toast.error(`Error: ${data.createResourceCenter.errors[0].message}`)
        }
    }, [data])

    useEffect(() => {
        if (error) toast.error(`Error: ${error}`)
    }, [error])

    const handleClose = () => {
        formik.resetForm()
        setPreview(null)
        setUploadProgress(0)
        onClose()
    }

    return (
        <Modal show={isOpen} onHide={handleClose} centered>
            <Modal.Header closeButton>
                <Modal.Title>Upload Campaign Media</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {uploadProgress > 0 && uploadProgress < 100 && (
                    <div className="mb-3">
                        <ProgressBar now={uploadProgress} label={`${uploadProgress}%`} animated variant="success" />
                    </div>
                )}
                <Form onSubmit={formik.handleSubmit}>
                    <Form.Group className="mb-3">
                        <Form.Label>File</Form.Label>
                        <Form.Control
                            type="file"
                            onChange={handleFileChange}
                            accept="image/*,video/*,application/pdf"
                            isInvalid={!!formik.errors.file && !!formik.touched.file}
                        />
                        <Form.Control.Feedback type="invalid">{formik.errors.file}</Form.Control.Feedback>
                        {preview && (
                            <div className="mt-2 text-center bg-light p-2 rounded">
                                <img src={preview} alt="Preview" style={{ maxHeight: '150px', maxWidth: '100%' }} />
                            </div>
                        )}
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Name</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="e.g. Summer Promo Header"
                            {...formik.getFieldProps('name')}
                            isInvalid={!!formik.errors.name && !!formik.touched.name}
                        />
                        <Form.Control.Feedback type="invalid">{formik.errors.name}</Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Check
                            type="checkbox"
                            id="isActive"
                            label="Visible for others (Public)"
                            checked={formik.values.isActive}
                            onChange={formik.handleChange}
                        />
                        <Form.Text className="text-muted">
                            Check this to ensure the file can be viewed by email recipients.
                        </Form.Text>
                    </Form.Group>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>Cancel</Button>
                <Button variant="primary" onClick={() => formik.handleSubmit()} disabled={loading}>
                    {loading ? <Spinner size="sm" animation="border" /> : 'Upload'}
                </Button>
            </Modal.Footer>
        </Modal>
    )
}
